/**
 * src/components/ui/SortsManagerModal.tsx
 * Manage sorts (formerly bins) — add, rename, reorder, delete
 * Accessible from the settings cog in VenueBottomNav (manager role only)
 */

import { useState, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchBins, createBin, deleteBin, renameBin } from '@/lib/events'
import type { VenueBin } from '@/lib/events'
import { supabase } from '@/lib/supabase'
import { Spinner } from '@/components/ui'
import { X, Plus, GripVertical, Trash2, Check } from 'lucide-react'

const PRESET_COLOURS = [
  '#60a5fa', // blue   — Social
  '#f472b6', // pink   — Press
  '#34d399', // green  — Approval
  '#fb923c', // orange — Print
  '#a78bfa', // purple — Hold
  '#facc15', // yellow
  '#f87171', // red
  '#94a3b8', // slate
]

interface Props {
  venueId: string
  onClose: () => void
}

async function savePositions(sorts: VenueBin[]) {
  for (let i = 0; i < sorts.length; i++) {
    await supabase.from('bins').update({ position: i }).eq('id', sorts[i].id)
  }
}

export function SortsManagerModal({ venueId, onClose }: Props) {
  const qc = useQueryClient()

  const { data: sorts = [], isLoading } = useQuery({
    queryKey: ['bins', venueId],
    queryFn:  () => fetchBins(venueId),
    staleTime: 0,
  })

  // Local editable copy
  const [local, setLocal]       = useState<VenueBin[] | null>(null)
  const [editingId, setEditing] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  const [newName, setNewName]   = useState('')
  const [newColor, setNewColor] = useState(PRESET_COLOURS[0])
  const [saving, setSaving]     = useState(false)
  const [status, setStatus]     = useState('')

  // Initialise local once loaded
  const list = local ?? sorts

  // ── Drag to reorder ──────────────────────────────────────────────────────
  const dragIdx = useRef<number | null>(null)

  function onDragStart(i: number) { dragIdx.current = i }

  function onDragOver(e: React.DragEvent, i: number) {
    e.preventDefault()
    if (dragIdx.current === null || dragIdx.current === i) return
    const next = [...list]
    const [moved] = next.splice(dragIdx.current, 1)
    next.splice(i, 0, moved)
    dragIdx.current = i
    setLocal(next)
  }

  async function onDragEnd() {
    dragIdx.current = null
    setSaving(true)
    await savePositions(list)
    qc.invalidateQueries({ queryKey: ['bins', venueId] })
    setSaving(false)
  }

  // ── Rename ───────────────────────────────────────────────────────────────
  function startEdit(sort: VenueBin) {
    setEditing(sort.id)
    setEditName(sort.name)
  }

  async function commitRename(id: string) {
    if (!editName.trim()) return
    await renameBin(id, editName.trim())
    setLocal(list.map(s => s.id === id ? { ...s, name: editName.trim() } : s))
    qc.invalidateQueries({ queryKey: ['bins', venueId] })
    setEditing(null)
  }

  // ── Add ──────────────────────────────────────────────────────────────────
  async function handleAdd() {
    if (!newName.trim()) return
    setSaving(true)
    const created = await createBin(venueId, newName.trim(), newColor)
    setLocal([...list, created])
    qc.invalidateQueries({ queryKey: ['bins', venueId] })
    setNewName('')
    setNewColor(PRESET_COLOURS[0])
    setSaving(false)
    setStatus('Sort added')
    setTimeout(() => setStatus(''), 2000)
  }

  // ── Delete ───────────────────────────────────────────────────────────────
  async function handleDelete(id: string) {
    await deleteBin(id)
    setLocal(list.filter(s => s.id !== id))
    qc.invalidateQueries({ queryKey: ['bins', venueId] })
  }

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 100,
      background: 'rgba(0,0,0,0.85)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
    }} onClick={onClose}>
      <div style={{
        width: '100%', maxWidth: 480,
        background: '#0f0f0f', borderRadius: '20px 20px 0 0',
        border: '1px solid rgba(255,255,255,0.08)',
        padding: '20px 16px 40px',
        maxHeight: '85vh', overflowY: 'auto',
      }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ flex: 1, color: 'white', fontWeight: 800, fontSize: 18, margin: 0 }}>
            Manage Sorts
          </h2>
          {saving && <Spinner size={14} />}
          {status && <span style={{ fontSize: 11, color: '#34d399', marginRight: 8 }}>{status}</span>}
          <button type="button" onClick={onClose}
            style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', padding: 4 }}>
            <X size={20} />
          </button>
        </div>

        {isLoading
          ? <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}><Spinner size={20} /></div>
          : (
          <>
            {/* Sort list */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 20 }}>
              {list.map((sort, i) => (
                <div key={sort.id}
                  draggable
                  onDragStart={() => onDragStart(i)}
                  onDragOver={e => onDragOver(e, i)}
                  onDragEnd={onDragEnd}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.07)',
                    borderRadius: 12, padding: '10px 12px',
                    cursor: 'grab',
                  }}>

                  <GripVertical size={14} style={{ color: 'rgba(255,255,255,0.2)', flexShrink: 0 }} />

                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: sort.color, flexShrink: 0 }} />

                  {editingId === sort.id
                    ? (
                      <input
                        autoFocus
                        value={editName}
                        onChange={e => setEditName(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') commitRename(sort.id); if (e.key === 'Escape') setEditing(null) }}
                        style={{
                          flex: 1, background: 'rgba(255,255,255,0.08)',
                          border: '1px solid rgba(255,255,255,0.2)',
                          borderRadius: 8, padding: '4px 8px',
                          color: 'white', fontSize: 13, outline: 'none',
                        }}
                      />
                    )
                    : (
                      <span
                        onClick={() => startEdit(sort)}
                        style={{ flex: 1, color: 'white', fontSize: 13, fontWeight: 600, cursor: 'text' }}>
                        {sort.name}
                      </span>
                    )
                  }

                  {editingId === sort.id
                    ? (
                      <button type="button" onClick={() => commitRename(sort.id)}
                        style={{ background: 'none', border: 'none', color: '#34d399', cursor: 'pointer', padding: 4 }}>
                        <Check size={16} />
                      </button>
                    )
                    : (
                      <button type="button" onClick={() => handleDelete(sort.id)}
                        style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.2)', cursor: 'pointer', padding: 4 }}>
                        <Trash2 size={14} />
                      </button>
                    )
                  }
                </div>
              ))}
            </div>

            {/* Add new sort */}
            <div style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.07)',
              borderRadius: 14, padding: '14px 12px',
            }}>
              <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 10px' }}>
                Add sort
              </p>

              {/* Colour picker */}
              <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                {PRESET_COLOURS.map(c => (
                  <button key={c} type="button" onClick={() => setNewColor(c)}
                    style={{
                      width: 22, height: 22, borderRadius: '50%', background: c,
                      border: newColor === c ? '2.5px solid white' : '2.5px solid transparent',
                      cursor: 'pointer', padding: 0,
                      boxShadow: newColor === c ? `0 0 8px ${c}` : 'none',
                    }} />
                ))}
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleAdd() }}
                  placeholder="Sort name…"
                  style={{
                    flex: 1, background: 'rgba(255,255,255,0.07)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: 10, padding: '9px 12px',
                    color: 'white', fontSize: 13, outline: 'none',
                  }}
                />
                <button type="button" onClick={handleAdd}
                  disabled={!newName.trim() || saving}
                  style={{
                    background: newName.trim() ? 'rgba(96,165,250,0.2)' : 'rgba(255,255,255,0.05)',
                    border: `1px solid ${newName.trim() ? 'rgba(96,165,250,0.5)' : 'rgba(255,255,255,0.08)'}`,
                    borderRadius: 10, padding: '9px 14px',
                    color: newName.trim() ? '#60a5fa' : 'rgba(255,255,255,0.2)',
                    cursor: newName.trim() ? 'pointer' : 'not-allowed',
                    display: 'flex', alignItems: 'center', gap: 6,
                    fontSize: 13, fontWeight: 700,
                  }}>
                  <Plus size={14} /> Add
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
